import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import { usePostData } from '@/api/apiHooks';
import { useAuth } from '@/auth';
import { PERMISSIONS_PREFIX } from '@/constants';
import { URLS } from '@/utils/urls';

interface Permission {
  action: string;
  resource: string;
  hasAllCompaniesAssigned: boolean;
}

interface PermissionsContextType {
  permissions: Permission[] | null;
  roles: string[];
  isLoading: boolean;
}

interface PermissionsProviderProps {
  children: ReactNode;
}

const PermissionsContext = createContext<PermissionsContextType | null>(null);

export const PermissionsProvider = ({ children }: PermissionsProviderProps) => {
  const { getAccessToken } = useAuth();
  const [permissions, setPermissions] = useState<Permission[] | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const { mutate: getPermissionsFromExchange } = usePostData({
    url: URLS.getPermissions(),
  });

  const getPermissions = async () => {
    const token = await getAccessToken();
    if (token) {
      const payload = new URLSearchParams();
      payload.append('token', token);
      getPermissionsFromExchange(payload, {
        onSuccess: (response) => {
          const data = response.data as {
            permissions?: string[];
            roles?: string[];
          };
          const permissionsFromExchange = data?.permissions ?? [];
          const permissionsArray = permissionsFromExchange.filter(
            (permission) => permission.includes(PERMISSIONS_PREFIX)
          );
          const permissionsToSet = permissionsArray.map(
            (permission: string) => {
              const [resource, action, assignedCompanies] =
                permission.split('#');
              return {
                resource: resource.replace(PERMISSIONS_PREFIX, ''),
                action,
                hasAllCompaniesAssigned: assignedCompanies
                  ? assignedCompanies.includes(':*')
                  : false,
              };
            }
          );
          setPermissions(permissionsToSet);
          setRoles(data?.roles ?? []);
        },
        onError: (error) => {
          console.error('Error fetching permissions:', error);
          setPermissions([]);
          setRoles([]);
        },
      });
    } else {
      throw new Error('Error fetching permissions from exchange API');
    }
  };

  useEffect(() => {
    getPermissions();
  }, []);

  useEffect(() => {
    if (permissions && isLoading) {
      setIsLoading(false);
    }
  }, [isLoading, permissions]);

  return (
    <PermissionsContext.Provider value={{ permissions, roles, isLoading }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = (): PermissionsContextType => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionsProvider');
  }

  return context;
};
